import { lazy, Suspense, useEffect } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import { BrowserRouter, Routes, Route } from "react-router-dom";

import Login from "../pages/Login";
import MainLayout from "../layouts/MainLayout";
import ProtectedRoute from "../auth/ProtectedRoute";
import { POS_INVENTORY_ROLES, POS_OPERATION_ROLES, POS_ROLES } from "../modules/pos/constants";
import { FLEET_ADMIN_ROLES, FLEET_TECH_ROLES } from "../modules/fleet/constants";
import { MCO_ADMIN_ROLES, MCO_ENABLED, MCO_ROLES } from "../modules/mco/constants";
import {
  loadAdminAssignmentSyncPage,
  loadAdminStatusSyncPage,
  loadAssignPartsPage,
  loadChangeApprovalsPage,
  loadDashboardPage,
  loadFleetAssignmentsPage,
  loadFleetDashboardPage,
  loadFleetFuelRecordsPage,
  loadFleetMaintenancePage,
  loadFleetMyFuelRecordsPage,
  loadFleetMyUsageLogsPage,
  loadFleetMyVehiclesPage,
  loadFleetReportsPage,
  loadFleetUsageLogsPage,
  loadFleetVehiclesPage,
  loadInventoryPage,
  loadMcoAssignmentsPage,
  loadMcoAuditLogPage,
  loadMcoBatchesPage,
  loadMcoBatchDetailPage,
  loadMcoDailyTotalsPage,
  loadMcoDashboardPage,
  loadMcoMismatchesPage,
  loadMcoPendingAssignmentsPage,
  loadMcoSettingsPage,
  loadMcoSitrekReportsPage,
  loadMcoSitrekReportDetailPage,
  loadMcoSystemPage,
  loadMcoTerminalDetailPage,
  loadMcoTerminalsPage,
  loadMegaPaySettingsPage,
  loadMegaPayTicketsPage,
  loadMyPartsPage,
  loadNotificationsPage,
  loadOfflineTerminalsPage,
  loadOldPendingPartsPage,
  loadPartPricesPage,
  loadPartsMovementReportPage,
  loadPosDashboardPage,
  loadPosDeviceReplacementsPage,
  loadPosInventoryMovementsPage,
  loadPosInventoryPage,
  loadPosProductsPage,
  loadPosPurchasesPage,
  loadPosRepairWorkspacePage,
  loadPosSerialChangeReportPage,
  loadPosTicketsPage,
  loadPosUsedPartsRegisterPage,
  loadProductsPage,
  loadPurchasesPage,
  loadRepairWarrantyPage,
  loadSettingsUsersPage,
  loadSoldPartsPage,
  loadTaskDetailPage,
  loadTasksPage,
  loadTechnicianPerformancePage,
  loadTerminalHealthPage,
  loadTerminalPerformancePage,
  loadTerminalsPage,
} from "./pageLoaders";

const Dashboard = lazy(loadDashboardPage);
const Notifications = lazy(loadNotificationsPage);
const Products = lazy(loadProductsPage);
const Purchases = lazy(loadPurchasesPage);
const Inventory = lazy(loadInventoryPage);
const MegaPayTickets = lazy(loadMegaPayTicketsPage);
const OfflineTerminals = lazy(loadOfflineTerminalsPage);
const Tasks = lazy(loadTasksPage);
const TaskDetail = lazy(loadTaskDetailPage);
const RepairWarranty = lazy(loadRepairWarrantyPage);
const SettingsUsers = lazy(loadSettingsUsersPage);
const AdminAssignmentSync = lazy(loadAdminAssignmentSyncPage);
const AdminStatusSync = lazy(loadAdminStatusSyncPage);
const MegaPaySettings = lazy(loadMegaPaySettingsPage);
const PartPrices = lazy(loadPartPricesPage);
const Terminals = lazy(loadTerminalsPage);
const AssignParts = lazy(loadAssignPartsPage);
const MyParts = lazy(loadMyPartsPage);
const ChangeApprovals = lazy(loadChangeApprovalsPage);
const OldPendingParts = lazy(loadOldPendingPartsPage);
const SoldParts = lazy(loadSoldPartsPage);
const TerminalHealth = lazy(loadTerminalHealthPage);
const TechnicianPerformance = lazy(loadTechnicianPerformancePage);
const TerminalPerformance = lazy(loadTerminalPerformancePage);
const PartsMovementReport = lazy(loadPartsMovementReportPage);
const FleetDashboard = lazy(loadFleetDashboardPage);
const FleetVehicles = lazy(loadFleetVehiclesPage);
const FleetMyVehicles = lazy(loadFleetMyVehiclesPage);
const FleetAssignments = lazy(loadFleetAssignmentsPage);
const FleetUsageLogs = lazy(loadFleetUsageLogsPage);
const FleetMyUsageLogs = lazy(loadFleetMyUsageLogsPage);
const FleetFuelRecords = lazy(loadFleetFuelRecordsPage);
const FleetMyFuelRecords = lazy(loadFleetMyFuelRecordsPage);
const FleetMaintenance = lazy(loadFleetMaintenancePage);
const FleetReports = lazy(loadFleetReportsPage);
const McoDashboard = lazy(loadMcoDashboardPage);
const McoBatches = lazy(loadMcoBatchesPage);
const McoBatchDetail = lazy(loadMcoBatchDetailPage);
const McoAssignments = lazy(loadMcoAssignmentsPage);
const McoAuditLog = lazy(loadMcoAuditLogPage);
const McoPendingAssignments = lazy(loadMcoPendingAssignmentsPage);
const McoDailyTotals = lazy(loadMcoDailyTotalsPage);
const McoTerminals = lazy(loadMcoTerminalsPage);
const McoMismatches = lazy(loadMcoMismatchesPage);
const McoSettings = lazy(loadMcoSettingsPage);
const McoSitrekReports = lazy(loadMcoSitrekReportsPage);
const McoSitrekReportDetail = lazy(loadMcoSitrekReportDetailPage);
const McoSystem = lazy(loadMcoSystemPage);
const McoTerminalDetail = lazy(loadMcoTerminalDetailPage);
const PosDashboard = lazy(loadPosDashboardPage);
const PosTickets = lazy(loadPosTicketsPage);
const PosRepairWorkspace = lazy(loadPosRepairWorkspacePage);
const PosProducts = lazy(loadPosProductsPage);
const PosInventory = lazy(loadPosInventoryPage);
const PosInventoryMovements = lazy(loadPosInventoryMovementsPage);
const PosPurchases = lazy(loadPosPurchasesPage);
const PosUsedPartsRegister = lazy(loadPosUsedPartsRegisterPage);
const PosDeviceReplacements = lazy(loadPosDeviceReplacementsPage);
const PosSerialChangeReport = lazy(loadPosSerialChangeReportPage);

function RouteLoader() {
  return (
    <Box
      sx={{
        minHeight: "40vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <CircularProgress />
    </Box>
  );
}

const withSuspense = (element) => (
  <Suspense fallback={<RouteLoader />}>
    {element}
  </Suspense>
);

export default function AppRoutes() {
  useEffect(() => {
    const preloadDashboard = () => {
      loadDashboardPage();
    };

    if (typeof window !== "undefined" && typeof window.requestIdleCallback === "function") {
      const idleId = window.requestIdleCallback(preloadDashboard, { timeout: 1200 });
      return () => window.cancelIdleCallback?.(idleId);
    }

    const timeoutId = window.setTimeout(preloadDashboard, 600);
    return () => window.clearTimeout(timeoutId);
  }, []);

  return (
    <BrowserRouter>
      <Routes>

        {/* Public */}
        <Route path="/" element={<Login />} />

        {/* Protected */}
        <Route element={<MainLayout />}>

          <Route
            path="/dashboard"
            element={
              <ProtectedRoute roles={["SUPER_ADMIN", "INVENTORY_ADMIN", "OPERATIONS_MANAGER"]}>
                {withSuspense(<Dashboard />)}
              </ProtectedRoute>
            }
          />

          <Route
            path="/notifications"
            element={
              <ProtectedRoute roles={["SUPER_ADMIN", "INVENTORY_ADMIN", "OPERATIONS_MANAGER", "FIELD_TECH", "OFFICE_TECH", "POS_TECHNICIAN"]}>
                {withSuspense(<Notifications />)}
              </ProtectedRoute>
            }
          />

          <Route
            path="/products"
            element={
              <ProtectedRoute roles={["SUPER_ADMIN", "INVENTORY_ADMIN"]}>
                {withSuspense(<Products />)}
              </ProtectedRoute>
            }
          />

          <Route
            path="/purchases"
            element={
              <ProtectedRoute roles={["SUPER_ADMIN", "INVENTORY_ADMIN", "OPERATIONS_MANAGER", "OFFICE_TECH"]}>
                {withSuspense(<Purchases />)}
              </ProtectedRoute>
            }
          />

          <Route
            path="/inventory"
            element={
              <ProtectedRoute roles={["SUPER_ADMIN", "INVENTORY_ADMIN", "OPERATIONS_MANAGER", "OFFICE_TECH"]}>
                {withSuspense(<Inventory />)}
              </ProtectedRoute>
            }
          />

          {/* TASKS */}
          <Route
            path="/tasks"
            element={
              <ProtectedRoute roles={["SUPER_ADMIN", "OPERATIONS_MANAGER", "FIELD_TECH", "OFFICE_TECH", "INVENTORY_ADMIN"]}>
                {withSuspense(<Tasks />)}
              </ProtectedRoute>
            }
          />

          <Route
            path="/megapay-tickets"
            element={
              <ProtectedRoute roles={["SUPER_ADMIN", "OPERATIONS_MANAGER", "FIELD_TECH", "OFFICE_TECH", "INVENTORY_ADMIN"]}>
                {withSuspense(<MegaPayTickets />)}
              </ProtectedRoute>
            }
          />

          <Route
            path="/pos/dashboard"
            element={
              <ProtectedRoute roles={POS_ROLES}>
                {withSuspense(<PosDashboard />)}
              </ProtectedRoute>
            }
          />

          <Route
            path="/pos/tickets"
            element={
              <ProtectedRoute roles={POS_ROLES}>
                {withSuspense(<PosTickets />)}
              </ProtectedRoute>
            }
          />

          <Route
            path="/pos/repairs"
            element={
              <ProtectedRoute roles={POS_OPERATION_ROLES}>
                {withSuspense(<PosRepairWorkspace />)}
              </ProtectedRoute>
            }
          />

          <Route
            path="/pos/products"
            element={
              <ProtectedRoute roles={POS_INVENTORY_ROLES}>
                {withSuspense(<PosProducts />)}
              </ProtectedRoute>
            }
          />

          <Route
            path="/pos/inventory"
            element={
              <ProtectedRoute roles={POS_INVENTORY_ROLES}>
                {withSuspense(<PosInventory />)}
              </ProtectedRoute>
            }
          />

          <Route
            path="/pos/inventory-movements"
            element={
              <ProtectedRoute roles={["SUPER_ADMIN"]}>
                {withSuspense(<PosInventoryMovements />)}
              </ProtectedRoute>
            }
          />

          <Route
            path="/pos/purchases"
            element={
              <ProtectedRoute roles={POS_INVENTORY_ROLES}>
                {withSuspense(<PosPurchases />)}
              </ProtectedRoute>
            }
          />

          <Route
            path="/pos/used-parts"
            element={
              <ProtectedRoute roles={POS_INVENTORY_ROLES}>
                {withSuspense(<PosUsedPartsRegister />)}
              </ProtectedRoute>
            }
          />

          <Route
            path="/pos/device-replacements"
            element={
              <ProtectedRoute roles={POS_OPERATION_ROLES}>
                {withSuspense(<PosDeviceReplacements />)}
              </ProtectedRoute>
            }
          />

          <Route
            path="/pos/serial-changes"
            element={
              <ProtectedRoute roles={POS_ROLES}>
                {withSuspense(<PosSerialChangeReport />)}
              </ProtectedRoute>
            }
          />

          <Route
            path="/fleet/dashboard"
            element={
              <ProtectedRoute roles={FLEET_ADMIN_ROLES}>
                {withSuspense(<FleetDashboard />)}
              </ProtectedRoute>
            }
          />

          <Route
            path="/fleet/vehicles"
            element={
              <ProtectedRoute roles={FLEET_ADMIN_ROLES}>
                {withSuspense(<FleetVehicles />)}
              </ProtectedRoute>
            }
          />

          <Route
            path="/fleet/my-vehicles"
            element={
              <ProtectedRoute roles={FLEET_TECH_ROLES}>
                {withSuspense(<FleetMyVehicles />)}
              </ProtectedRoute>
            }
          />

          <Route
            path="/fleet/assignments"
            element={
              <ProtectedRoute roles={FLEET_ADMIN_ROLES}>
                {withSuspense(<FleetAssignments />)}
              </ProtectedRoute>
            }
          />

          <Route
            path="/fleet/usage-logs"
            element={
              <ProtectedRoute roles={FLEET_ADMIN_ROLES}>
                {withSuspense(<FleetUsageLogs />)}
              </ProtectedRoute>
            }
          />

          <Route
            path="/fleet/my-usage-logs"
            element={
              <ProtectedRoute roles={FLEET_TECH_ROLES}>
                {withSuspense(<FleetMyUsageLogs />)}
              </ProtectedRoute>
            }
          />

          <Route
            path="/fleet/fuel-records"
            element={
              <ProtectedRoute roles={FLEET_ADMIN_ROLES}>
                {withSuspense(<FleetFuelRecords />)}
              </ProtectedRoute>
            }
          />

          <Route
            path="/fleet/my-fuel-records"
            element={
              <ProtectedRoute roles={FLEET_TECH_ROLES}>
                {withSuspense(<FleetMyFuelRecords />)}
              </ProtectedRoute>
            }
          />

          <Route
            path="/fleet/maintenance"
            element={
              <ProtectedRoute roles={FLEET_ADMIN_ROLES}>
                {withSuspense(<FleetMaintenance />)}
              </ProtectedRoute>
            }
          />

          <Route
            path="/fleet/reports"
            element={
              <ProtectedRoute roles={FLEET_ADMIN_ROLES}>
                {withSuspense(<FleetReports />)}
              </ProtectedRoute>
            }
          />

          {MCO_ENABLED && (
            <>
              <Route
                path="/mco/dashboard"
                element={
                  <ProtectedRoute roles={MCO_ROLES}>
                    {withSuspense(<McoDashboard />)}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/mco/batches"
                element={
                  <ProtectedRoute roles={MCO_ROLES}>
                    {withSuspense(<McoBatches />)}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/mco/batches/:id"
                element={
                  <ProtectedRoute roles={MCO_ROLES}>
                    {withSuspense(<McoBatchDetail />)}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/mco/assignments"
                element={
                  <ProtectedRoute roles={MCO_ROLES}>
                    {withSuspense(<McoAssignments />)}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/mco/pending-assignments"
                element={
                  <ProtectedRoute roles={MCO_ROLES}>
                    {withSuspense(<McoPendingAssignments />)}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/mco/daily-totals"
                element={
                  <ProtectedRoute roles={MCO_ROLES}>
                    {withSuspense(<McoDailyTotals />)}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/mco/terminals"
                element={
                  <ProtectedRoute roles={MCO_ROLES}>
                    {withSuspense(<McoTerminals />)}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/mco/terminals/:megapayId"
                element={
                  <ProtectedRoute roles={MCO_ROLES}>
                    {withSuspense(<McoTerminalDetail />)}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/mco/mismatches"
                element={
                  <ProtectedRoute roles={MCO_ROLES}>
                    {withSuspense(<McoMismatches />)}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/mco/settings"
                element={
                  <ProtectedRoute roles={MCO_ADMIN_ROLES}>
                    {withSuspense(<McoSettings />)}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/mco/sitrek"
                element={
                  <ProtectedRoute roles={MCO_ROLES}>
                    {withSuspense(<McoSitrekReports />)}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/mco/sitrek/:id"
                element={
                  <ProtectedRoute roles={MCO_ROLES}>
                    {withSuspense(<McoSitrekReportDetail />)}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/mco/system"
                element={
                  <ProtectedRoute roles={MCO_ADMIN_ROLES}>
                    {withSuspense(<McoSystem />)}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/mco/audit"
                element={
                  <ProtectedRoute roles={MCO_ADMIN_ROLES}>
                    {withSuspense(<McoAuditLog />)}
                  </ProtectedRoute>
                }
              />
            </>
          )}

          <Route
            path="/offline-terminals"
            element={
              <ProtectedRoute roles={["SUPER_ADMIN", "OPERATIONS_MANAGER"]}>
                {withSuspense(<OfflineTerminals />)}
              </ProtectedRoute>
            }
          />

          <Route
            path="/task/:id"
            element={
              <ProtectedRoute roles={["SUPER_ADMIN", "OPERATIONS_MANAGER", "FIELD_TECH", "OFFICE_TECH", "INVENTORY_ADMIN"]}>
                {withSuspense(<TaskDetail />)}
              </ProtectedRoute>
            }
          />

          {/* INVENTORY FLOW */}
          <Route
            path="/assign-parts"
            element={
              <ProtectedRoute roles={["SUPER_ADMIN", "INVENTORY_ADMIN", "OFFICE_TECH"]}>
                {withSuspense(<AssignParts />)}
              </ProtectedRoute>
            }
          />

          <Route
            path="/my-parts"
            element={
              <ProtectedRoute roles={["FIELD_TECH", "SUPER_ADMIN"]}>
                {withSuspense(<MyParts />)}
              </ProtectedRoute>
            }
          />
          <Route
            path="/change-approvals"
            element={
              <ProtectedRoute roles={["SUPER_ADMIN", "OPERATIONS_MANAGER", "INVENTORY_ADMIN", "OFFICE_TECH"]}>
                {withSuspense(<ChangeApprovals />)}
              </ProtectedRoute>
            }
          />
          <Route
            path="/old-pending"
            element={
              <ProtectedRoute roles={["SUPER_ADMIN", "OPERATIONS_MANAGER", "INVENTORY_ADMIN", "OFFICE_TECH"]}>
                {withSuspense(<OldPendingParts />)}
              </ProtectedRoute>
            }
          />

          <Route
            path="/sold-parts"
            element={
              <ProtectedRoute roles={["SUPER_ADMIN", "OPERATIONS_MANAGER", "INVENTORY_ADMIN"]}>
                {withSuspense(<SoldParts />)}
              </ProtectedRoute>
            }
          />

          {/* OTHER */}
          <Route
            path="/repair-warranty"
            element={
              <ProtectedRoute roles={["SUPER_ADMIN", "INVENTORY_ADMIN", "OFFICE_TECH"]}>
                {withSuspense(<RepairWarranty />)}
              </ProtectedRoute>
            }
          />

          <Route
            path="/reports"
            element={
              <ProtectedRoute roles={["SUPER_ADMIN", "OPERATIONS_MANAGER"]}>
                {withSuspense(<TerminalHealth />)}
              </ProtectedRoute>
            }
          />

          <Route
            path="/technician-performance"
            element={
              <ProtectedRoute roles={["SUPER_ADMIN", "OPERATIONS_MANAGER"]}>
                {withSuspense(<TechnicianPerformance />)}
              </ProtectedRoute>
            }
          />

          <Route
            path="/terminal-performance"
            element={
              <ProtectedRoute roles={["SUPER_ADMIN"]}>
                {withSuspense(<TerminalPerformance />)}
              </ProtectedRoute>
            }
          />

          <Route
            path="/parts-movement"
            element={
              <ProtectedRoute roles={["SUPER_ADMIN"]}>
                {withSuspense(<PartsMovementReport />)}
              </ProtectedRoute>
            }
          />

          <Route
            path="/settings-users"
            element={
              <ProtectedRoute roles={["SUPER_ADMIN"]}>
                {withSuspense(<SettingsUsers />)}
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/megapay-assignment-sync"
            element={
              <ProtectedRoute roles={["SUPER_ADMIN"]}>
                {withSuspense(<AdminAssignmentSync />)}
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/megapay-status-sync"
            element={
              <ProtectedRoute roles={["SUPER_ADMIN"]}>
                {withSuspense(<AdminStatusSync />)}
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/megapay-settings"
            element={
              <ProtectedRoute roles={["SUPER_ADMIN"]}>
                {withSuspense(<MegaPaySettings />)}
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/part-prices"
            element={
              <ProtectedRoute roles={["SUPER_ADMIN"]}>
                {withSuspense(<PartPrices />)}
              </ProtectedRoute>
            }
          />

          <Route
            path="/terminals"
            element={
              <ProtectedRoute roles={["SUPER_ADMIN", "OPERATIONS_MANAGER", "INVENTORY_ADMIN", "OFFICE_TECH", "FIELD_TECH"]}>
                {withSuspense(<Terminals />)}
              </ProtectedRoute>
            }
          />

        </Route>
      </Routes>
    </BrowserRouter>
  );
}
